import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  Tooltip,
  Avatar,
  CardMedia,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import UploadTwoToneIcon from '@mui/icons-material/UploadTwoTone';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { RootState } from 'src/redux-store/appStore';
import { setUserDetails } from 'src/redux-store/slices/userSlice';

const Input = styled('input')({
  display: 'none'
});

const AvatarWrapper = styled(Card)(
  ({ theme }) => `

    position: relative;
    overflow: visible;
    display: inline-block;
    margin-top: -${theme.spacing(9)};
    margin-left: ${theme.spacing(2)};

    .MuiAvatar-root {
      width: ${theme.spacing(16)};
      height: ${theme.spacing(16)};
    }
`
);

const ButtonUploadWrapper = styled(Box)(
  ({ theme }) => `
    position: absolute;
    width: ${theme.spacing(4)};
    height: ${theme.spacing(4)};
    bottom: -${theme.spacing(1)};
    right: -${theme.spacing(1)};

    .MuiIconButton-root {
      border-radius: 100%;
      background: ${theme.colors.primary.main};
      color: ${theme.palette.primary.contrastText};
      box-shadow: ${theme.colors.shadows.primary};
      width: ${theme.spacing(4)};
      height: ${theme.spacing(4)};
      padding: 0;
  
      &:hover {
        background: ${theme.colors.primary.dark};
      }
    }
`
);

const CardCover = styled(Card)(
  ({ theme }) => `
    position: relative;

    .MuiCardMedia-root {
      height: ${theme.spacing(26)};
    }
`
);

const CardCoverAction = styled(Box)(
  ({ theme }) => `
    position: absolute;
    right: ${theme.spacing(2)};
    bottom: ${theme.spacing(2)};
`
);

const ProfileCover = ({ user }) => {
  const navigate = useNavigate();
  const { townshipId } = useParams();
  const dispatch = useDispatch();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const defaultAvatar = '/static/images/avatars/default-avatar.png';
  const [avatarSrc, setAvatarSrc] = useState(user.avatar || defaultAvatar);
  const [uploading, setUploading] = useState(false);

  // Update avatar when user prop changes
  useEffect(() => {
    setAvatarSrc(user.avatar || defaultAvatar);
  }, [user.avatar]);

  const handleBack = () => {
    navigate(`/dashboard/${townshipId}`);
  };

  const handleAvatarError = () => {
    // Fallback to default avatar if image fails to load
    if (avatarSrc !== defaultAvatar) {
      setAvatarSrc(defaultAvatar);
    }
  };

const handleProfilePicChange = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Preview immediately
  const previewUrl = URL.createObjectURL(file);
  setAvatarSrc(previewUrl);

  const formData = new FormData();
  formData.append('profilePicture', file); // MUST MATCH BACKEND

  try {
    setUploading(true);

    const response = await httpRequest(
      'PUT',
      '/auth/profile/upload-picture',
      formData,
      {
        'Content-Type': 'multipart/form-data'
      }
    );

    const uploadedUrl = response.data?.downloadURL;

    if (uploadedUrl) {
      // Update local avatar preview
      setAvatarSrc(uploadedUrl);

      // Also update the avatar URL in Redux so it reflects everywhere (e.g. header userbox)
      if (userDetails) {
        dispatch(
          setUserDetails({
            ...userDetails,
            ProfilePicture: uploadedUrl
          })
        );
      }

      toast.success('Profile picture updated');
    }
  } catch (error) {
    toast.error('Failed to upload profile picture');
    setAvatarSrc(user.avatar || defaultAvatar);
  } finally {
    setUploading(false);
  }
};

  return (
    <>
      <Box display="flex" alignItems={'center'} mb={{ xs: 1, sm: 3 }}>
        <Tooltip arrow placement="top" title="Go back">
          <IconButton
            color="primary"
            sx={{ p: 2, mr: 2 }}
            onClick={() => handleBack()}
          >
            <ArrowBackTwoToneIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography fontSize={"1.4rem"} gutterBottom>
            Hello, <b>{user.name}</b>
          </Typography>
        </Box>
      </Box>
      <Typography variant="subtitle2" mb={1}>
        This is your profile page – easy to edit and always fast.
      </Typography>
      <CardCover>
        <CardMedia image={user.coverImg} />
        <CardCoverAction>
          <Input accept="image/*" id="change-cover" multiple type="file" />
          <label htmlFor="change-cover">
            <CustomButtonContained
              startIcon={<UploadTwoToneIcon />}
              component="span"
              title={'Change cover'}
            />
          </label>
        </CardCoverAction>
      </CardCover>
      <AvatarWrapper>
        <Avatar
          variant="rounded"
          alt={user.name}
          src={avatarSrc}
          onError={handleAvatarError}
        />
        <ButtonUploadWrapper>
          <Input
            accept="image/*"
            id="icon-button-file"
            name="profilePicture"
            type="file"
            onChange={handleProfilePicChange}
          />
          <label htmlFor="icon-button-file">
            <IconButton component="span" color="primary" disabled={uploading}>
              <UploadTwoToneIcon />
            </IconButton>
          </label>
        </ButtonUploadWrapper>
      </AvatarWrapper>
      <Box py={2} pl={2} mb={3}>
        <Typography gutterBottom variant="h4">
          {user.name}
        </Typography>
        <Typography variant="subtitle2">{user.description}</Typography>

      </Box>
    </>
  );
};

ProfileCover.propTypes = {
  user: PropTypes.object.isRequired
};

export default ProfileCover;
